import { useEffect, useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { Button, Dialog, DialogActions, DialogContent, MenuItem, Stack, TextField, Typography } from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'

import locales from '@/assets/locales'
import { Patient } from '@/core/models/patient.model'
import { useUpdatePatientMutation } from '@/core/services/patient'

type CreatePatientAccountDialogProps = {
  open: boolean
  onClose: () => void
  patient: Patient
  onAccountCreated: () => void
}

const LOCALE_KEYS = Object.keys(locales)

export function CreatePatientAccountDialog({ open, onClose, patient, onAccountCreated }: CreatePatientAccountDialogProps) {
  const [email, setEmail] = useState('')
  const [language, setLanguage] = useState('')
  const [updatePatient, { isLoading }] = useUpdatePatientMutation()

  useEffect(() => {
    if (open) {
      setEmail(patient.email ?? '')
      setLanguage(patient.userInfo?.language ?? '')
    }
  }, [open, patient])

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const hasEmail = !!patient.email
  const needsUpdate = !hasEmail || language !== (patient.userInfo?.language ?? '')

  const handleSubmit = async () => {
    if (!isValidEmail || !language) return
    try {
      if (needsUpdate) {
        await updatePatient({ id: patient._id, ...(!hasEmail && { email }), userInfo: { language } }).unwrap()
      }
      onAccountCreated()
      handleClose()
    } catch {
      // update failure is handled by RTK Query
    }
  }

  const handleClose = () => {
    setEmail('')
    setLanguage('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="h6">
            <Trans>Create patient account</Trans>
          </Typography>
          {!hasEmail && (
            <TextField
              label={t`Email`}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!email && !isValidEmail}
              helperText={email && !isValidEmail ? t`Please enter a valid email address` : undefined}
              fullWidth
              autoFocus
            />
          )}
          <TextField
            select
            label={t`Language`}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            fullWidth
            autoFocus={hasEmail}
          >
            {LOCALE_KEYS.map((locale) => (
              <MenuItem key={locale} value={locale}>
                {locale.toUpperCase()}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          <Trans>Cancel</Trans>
        </Button>
        <LoadingButton variant="contained" loading={isLoading} onClick={handleSubmit} disabled={!isValidEmail || !language}>
          <Trans>Create account</Trans>
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
